import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Award, CheckCircle, FileText, Download, Eye } from 'lucide-react';

const examDetails = {
  1: {
    title: 'Chemistry Unit Test',
    subject: 'Chemistry',
    date: '2024-03-15',
    duration: 60,
    maxMarks: 100,
    marksObtained: 85,
    timeTaken: 55,
    questions: [
      { id: 1, question: 'Define the concept of molarity.', marks: 5, obtainedMarks: 5, pdfUploaded: 'molarity_answer.pdf' },
      { id: 2, question: 'Explain the difference between exothermic and endothermic reactions.', marks: 10, obtainedMarks: 8, pdfUploaded: 'exo_endo_reactions.pdf' },
      { id: 3, question: 'Balance the following chemical equation: Fe + O2 → Fe2O3', marks: 5, obtainedMarks: 5, pdfUploaded: 'balancing_equation.pdf' },
      { id: 4, question: 'Describe the structure of an atom.', marks: 10, obtainedMarks: 9, pdfUploaded: 'atom_structure.pdf' },
      { id: 5, question: 'What is the pH scale? Explain its significance.', marks: 10, obtainedMarks: 8, pdfUploaded: 'ph_scale.pdf' },
    ]
  },
  // Add more exam details as needed
};

export default function ExamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const exam = examDetails[id];

  if (!exam) {
    return <div>Exam not found</div>;
  }

  const handleDownload = (pdfName) => {
    // Implement download functionality here
    console.log(`Downloading ${pdfName}`);
  };

  const handleView = (pdfName) => {
    // Implement view functionality here
    console.log(`Viewing ${pdfName}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/exams')}
          className="mb-8 flex items-center text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span className="text-lg font-medium">Back to Exams</span>
        </button>

        <div className="bg-white shadow-2xl rounded-3xl overflow-hidden">
          <div className="p-8 sm:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{exam.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                {exam.subject}
              </span>
              <span className="text-gray-600 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                {exam.duration} minutes
              </span>
              <span className="text-gray-600 flex items-center">
                <Award className="w-5 h-5 mr-2" />
                Max Marks: {exam.maxMarks}
              </span>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 mb-8 shadow-md">
              <h2 className="text-2xl font-semibold text-indigo-900 mb-4">Exam Summary</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center bg-white p-4 rounded-lg shadow-sm">
                  <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
                  <div>
                    <span className="text-sm text-gray-500">Marks Obtained</span>
                    <p className="text-2xl font-bold text-indigo-600">{exam.marksObtained}/{exam.maxMarks}</p>
                  </div>
                </div>
                <div className="flex items-center bg-white p-4 rounded-lg shadow-sm">
                  <Clock className="w-8 h-8 text-indigo-500 mr-3" />
                  <div>
                    <span className="text-sm text-gray-500">Time Taken</span>
                    <p className="text-2xl font-bold text-indigo-600">{exam.timeTaken}/{exam.duration} min</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Question-wise Breakdown</h2>
            <div className="space-y-6">
              {exam.questions.map((q, index) => (
                <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Question {index + 1}</h3>
                  <p className="text-gray-700 mb-4">{q.question}</p>
                  <div className="flex flex-wrap justify-between items-center mb-4">
                    <span className="text-sm text-gray-600">Max Marks: {q.marks}</span>
                    <span className="text-sm font-medium text-indigo-600 bg-indigo-100 px-3 py-1 rounded-full">
                      Marks Obtained: {q.obtainedMarks}/{q.marks}
                    </span>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-indigo-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">{q.pdfUploaded}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDownload(q.pdfUploaded)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium transition duration-300 ease-in-out flex items-center"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </button>
                      <button
                        onClick={() => handleView(q.pdfUploaded)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium transition duration-300 ease-in-out flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}